<template>
  <div
    class="md:hidden bg-white text-black dark:bg-[#0a0a0d] dark:text-white overflow-x-hidden transition-colors duration-500"
  >
    <div
      class="fixed inset-0 -z-10 bg-gradient-to-b from-gray-100 via-white to-gray-100 dark:from-black dark:via-[#0f172a] dark:to-black transition-colors duration-500"
    ></div>

    <header
      class="fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl dark:border-white/5 dark:bg-black/50"
    >
      <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
        <div class="rounded-[2rem] p-3">
          <p class="mt-1 text-sm font-semibold tracking-[0.18em]">DAANYAAL KELLERMAN</p>
        </div>
      </div>
    </header>

    <main class="px-4 pb-20 pt-24">
      <section id="about" class="pt-6">
        <p
          class="text-[0.7rem] uppercase tracking-[0.38em] text-gray-500 dark:text-gray-400"
        >
          Mid-Level Full-Stack Developer
        </p>
        <h1 class="mt-5 text-4xl font-bold leading-tight">
          Building production systems for real teams.
        </h1>
        <p class="mt-5 max-w-md text-base leading-7 text-gray-600 dark:text-gray-300">
          I design and ship product, platform, and internal tooling work across React,
          Vue, Next.js, Node.js, PHP, and relational data systems.
        </p>

        <div class="mt-8 flex flex-col gap-3">
          <a
            href="#projects"
            class="inline-flex items-center justify-center rounded-full border border-black px-6 py-3 text-xs uppercase tracking-[0.25em] transition hover:bg-black hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-black"
          >
            View Work
          </a>
          <a
            :href="cvUrl"
            download
            class="inline-flex items-center justify-center rounded-full border border-black/10 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gray-600 transition hover:border-black hover:text-black dark:border-white/10 dark:text-gray-300 dark:hover:border-white dark:hover:text-white"
          >
            Download CV
          </a>
          <a
            href="#contact"
            class="inline-flex items-center justify-center rounded-full border border-black/10 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gray-600 transition dark:border-white/10 dark:text-gray-300"
          >
            Let’s Talk
          </a>
        </div>

        <div
          class="mt-8 overflow-hidden rounded-[2rem] border border-black/10 shadow-2xl dark:border-white/10"
        >
          <img
            src="/DK-OTHER.webp"
            alt="Daanyaal Kellerman portrait"
            class="h-[26rem] w-full object-cover object-top"
          />
        </div>
      </section>

      <section
        id="projects"
        class="border-t border-black/5 pb-16 pt-20 dark:border-white/5"
      >
        <p
          class="text-[0.7rem] uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400"
        >
          Selected Work
        </p>
        <h2 class="mt-4 text-3xl font-bold">Case Studies</h2>
        <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
          A tighter snapshot of how I approach product delivery, internal tooling, and
          production frontend work.
        </p>

        <div v-if="projects.length > 0" class="mt-8 space-y-6">
          <article
            v-for="(project, index) in projects"
            :key="index"
            class="relative overflow-hidden rounded-[2rem] border border-black/10 bg-white/80 p-4 shadow-[0_22px_70px_-52px_rgba(15,23,42,0.55)] backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04]"
          >
            <div class="absolute inset-0"></div>

            <div class="relative">
              <div class="mb-4 flex items-center justify-between gap-3">
                <span
                  class="rounded-full border border-black/10 bg-black/[0.04] px-3 py-1 text-[0.65rem] uppercase tracking-[0.28em] text-gray-600 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-300"
                >
                  {{ project.category || "Featured Project" }}
                </span>
                <span
                  class="text-[0.65rem] uppercase tracking-[0.32em] text-gray-500 dark:text-gray-400"
                >
                  {{ String(index + 1).padStart(2, "0") }}
                </span>
              </div>

              <div
                class="relative overflow-hidden rounded-[1.5rem] border border-black/10 dark:border-white/10"
              >
                <div
                  v-if="!project.loaded"
                  class="absolute inset-0 animate-pulse bg-gray-300/70 dark:bg-gray-800/70"
                ></div>
                <img
                  :src="project.image"
                  :alt="`${project.title} project preview`"
                  loading="lazy"
                  decoding="async"
                  @load="project.loaded = true"
                  class="h-56 w-full"
                  :class="[project.loaded ? 'opacity-100' : 'opacity-0', project.imageFit === 'contain' ? 'object-contain bg-white p-6' : 'object-cover object-top']"
                />
              </div>

              <h3 class="mt-6 text-2xl font-semibold leading-tight">
                {{ project.title }}
              </h3>

              <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
                {{ project.description }}
              </p>

              <div
                v-if="project.highlights && project.highlights.length"
                class="mt-5 space-y-3"
              >
                <div
                  v-for="(highlight, highlightIndex) in project.highlights"
                  :key="highlightIndex"
                  class="rounded-2xl border border-black/10 bg-black/[0.03] px-4 py-3 text-sm text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200"
                >
                  {{ highlight }}
                </div>
              </div>

              <div class="mt-6 flex flex-wrap gap-2.5">
                <span
                  v-for="(tech, techIndex) in project.stack"
                  :key="techIndex"
                  class="rounded-full border border-black/10 px-3 py-2 text-[0.68rem] uppercase tracking-[0.18em] text-gray-600 dark:border-white/10 dark:text-gray-300"
                >
                  {{ tech }}
                </span>
              </div>

              <div class="mt-6 flex flex-col gap-3">
                <a
                  v-if="project.live"
                  :href="project.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center justify-center rounded-full border border-black px-6 py-3 text-xs uppercase tracking-[0.25em] transition hover:bg-black hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-black"
                >
                  View Live Site
                </a>

                <a
                  v-if="project.github"
                  :href="project.github"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center justify-center rounded-full border border-black/10 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gray-600 transition hover:border-black hover:text-black dark:border-white/10 dark:text-gray-300 dark:hover:border-white dark:hover:text-white"
                >
                  Source Code
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section
        id="experience"
        class="border-t border-black/5 pb-16 pt-20 dark:border-white/5"
      >
        <p
          class="text-[0.7rem] uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400"
        >
          Experience
        </p>
        <h2 class="mt-4 text-3xl font-bold">Professional Journey</h2>
        <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
          A progression from structured training into agency delivery and then
          mid-level ownership in production teams.
        </p>

        <div class="mt-8 space-y-5">
          <article
            v-for="(job, index) in experience"
            :key="index"
            class="rounded-[1.75rem] border border-black/10 bg-black/[0.03] p-5 dark:border-white/10 dark:bg-white/[0.04]"
          >
            <p
              class="text-[0.65rem] uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400"
            >
              {{ job.period }}
            </p>
            <h3 class="mt-3 text-xl font-semibold leading-tight">
              {{ job.role }}
            </h3>
            <p class="mt-3 text-sm leading-7 text-gray-600 dark:text-gray-300">
              {{ job.description }}
            </p>
            <ul v-if="job.highlights?.length" class="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-gray-600 dark:text-gray-300">
              <li v-for="highlight in job.highlights" :key="highlight">{{ highlight }}</li>
            </ul>
            <p v-if="job.stack?.length" class="mt-4 text-xs leading-6 text-gray-500 dark:text-gray-400">
              <span class="font-semibold">Technologies:</span> {{ job.stack.join(", ") }}
            </p>
          </article>
        </div>
      </section>

      <section
        id="skills"
        class="border-t border-black/5 pb-14 pt-20 dark:border-white/5"
      >
        <div>
          <p
            class="text-[0.7rem] uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400"
          >
            Skills
          </p>
          <h2 class="mt-4 text-3xl font-bold">Core Skills</h2>
          <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
            The stack I rely on when the job spans interface quality, backend logic,
            data structure, and dependable release flow.
          </p>

          <div class="mt-6 grid grid-cols-3 gap-3">
            <div
              class="rounded-2xl border border-black/10 bg-black/[0.03] px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <p
                class="text-[0.6rem] uppercase tracking-[0.26em] text-gray-500 dark:text-gray-400"
              >
                Areas
              </p>
              <p class="mt-2 text-2xl font-semibold">{{ skills.length }}</p>
            </div>
            <div
              class="rounded-2xl border border-black/10 bg-black/[0.03] px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <p
                class="text-[0.6rem] uppercase tracking-[0.26em] text-gray-500 dark:text-gray-400"
              >
                Tools
              </p>
              <p class="mt-2 text-2xl font-semibold">{{ totalSkillItems }}</p>
            </div>
            <div
              class="rounded-2xl border border-black/10 bg-black/[0.03] px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <p
                class="text-[0.6rem] uppercase tracking-[0.26em] text-gray-500 dark:text-gray-400"
              >
                Focus
              </p>
              <p
                class="mt-2 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-gray-700 dark:text-gray-200"
              >
                Full Stack
              </p>
            </div>
          </div>
        </div>

        <div class="mt-6 space-y-4">
          <article
            v-for="(skill, index) in skills"
            :key="index"
            class="relative overflow-hidden rounded-[1.75rem] border border-black/10 bg-white/80 p-5 shadow-[0_20px_60px_-50px_rgba(15,23,42,0.55)] dark:border-white/10 dark:bg-white/[0.04]"
          >
            <div class="absolute inset-0"></div>
            <div class="relative">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p
                    class="text-[0.65rem] uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400"
                  >
                    {{ String(index + 1).padStart(2, "0") }}
                  </p>
                  <h3 class="mt-3 text-xl font-semibold">
                    {{ skill.category }}
                  </h3>
                </div>
                <div
                  class="rounded-full border border-black/10 bg-black/[0.03] px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.25em] text-gray-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300"
                >
                  {{ skill.items.length }} tools
                </div>
              </div>

              <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
                {{ skill.summary }}
              </p>

              <ul class="mt-5 flex flex-wrap gap-2.5">
                <li
                  v-for="(item, itemIndex) in skill.items"
                  :key="itemIndex"
                  class="rounded-full border border-black/10 bg-black/[0.03] px-3 py-2 text-[0.68rem] uppercase tracking-[0.18em] text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
          </article>
        </div>
      </section>

      <section
        id="contact"
        class="border-t border-black/5 pb-10 pt-16 dark:border-white/5"
      >
        <p
          class="text-[0.7rem] uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400"
        >
          Contact
        </p>
        <h2 class="mt-4 text-3xl font-bold">Let’s build something useful.</h2>
        <p class="mt-4 text-sm leading-7 text-gray-600 dark:text-gray-300">
          Available for product work, platform improvements, and internal tooling.
        </p>

        <div class="mt-6 space-y-3">
          <div
            v-for="card in contactCards"
            :key="card.label"
            class="rounded-[1.5rem] border border-black/10 bg-white/80 px-5 py-4 dark:border-white/10 dark:bg-white/[0.04]"
          >
            <p
              class="text-[0.62rem] uppercase tracking-[0.26em] text-gray-500 dark:text-gray-400"
            >
              {{ card.label }}
            </p>
            <a
              v-if="card.href"
              :href="card.href"
              class="mt-2 block text-sm font-medium text-gray-800 dark:text-gray-100"
            >
              {{ card.value }}
            </a>
            <p v-else class="mt-2 text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ card.value }}
            </p>
          </div>
        </div>

        <div class="mt-8 flex flex-wrap gap-4">
          <a
            v-for="link in socialLinks"
            :key="link.label"
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="link.label"
            :title="link.label"
            class="inline-flex h-14 w-14 items-center justify-center rounded-full border border-black/10 bg-black/[0.03] text-gray-700 transition hover:border-black hover:bg-black hover:text-white dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200 dark:hover:border-white dark:hover:bg-white dark:hover:text-black"
          >
            <svg
              v-if="link.icon === 'linkedin'"
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="h-5 w-5 fill-current"
            >
              <path
                d="M6.94 8.5H3.56V20h3.38V8.5ZM5.25 3A2.01 2.01 0 0 0 3.25 5c0 1.1.9 2 2 2s2-.9 2-2c0-1.1-.9-2-2-2ZM20.75 13.06c0-3.47-2.2-4.91-4.57-4.91-1.91 0-3.07 1.05-3.57 1.79V8.5H9.37c.04.95 0 11.5 0 11.5h3.24v-6.42c0-.34.02-.68.13-.92.27-.68.9-1.39 1.95-1.39 1.38 0 1.93 1.05 1.93 2.59V20h3.24v-6.94Z"
              />
            </svg>
            <svg
              v-else-if="link.icon === 'github'"
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="h-5 w-5 fill-current"
            >
              <path
                d="M12 2C6.48 2 2 6.58 2 12.22c0 4.5 2.87 8.32 6.84 9.66.5.1.68-.22.68-.49 0-.24-.01-1.04-.01-1.88-2.78.62-3.37-1.2-3.37-1.2-.45-1.18-1.11-1.49-1.11-1.49-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.9 1.57 2.36 1.12 2.94.86.09-.67.35-1.12.63-1.38-2.22-.26-4.56-1.14-4.56-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.7 0 0 .84-.28 2.75 1.05A9.3 9.3 0 0 1 12 6.97c.85 0 1.7.12 2.5.36 1.9-1.33 2.74-1.05 2.74-1.05.56 1.4.21 2.44.1 2.7.64.72 1.03 1.63 1.03 2.75 0 3.93-2.34 4.79-4.58 5.05.36.32.68.94.68 1.9 0 1.37-.01 2.47-.01 2.8 0 .27.18.6.69.49A10.23 10.23 0 0 0 22 12.22C22 6.58 17.52 2 12 2Z"
              />
            </svg>
          </a>
        </div>

        <form @submit.prevent="submitForm" class="mt-8 space-y-4 text-left">
          <input
            v-model="form.name"
            type="text"
            placeholder="Your name"
            required
            class="w-full rounded-2xl border border-black/10 bg-black/[0.03] px-5 py-4 focus:outline-none focus:border-black dark:border-white/10 dark:bg-white/[0.04] dark:focus:border-white"
          />

          <input
            v-model="form.email"
            type="email"
            placeholder="Your email"
            required
            class="w-full rounded-2xl border border-black/10 bg-black/[0.03] px-5 py-4 focus:outline-none focus:border-black dark:border-white/10 dark:bg-white/[0.04] dark:focus:border-white"
          />

          <textarea
            v-model="form.message"
            rows="5"
            placeholder="Your message"
            required
            class="w-full rounded-2xl border border-black/10 bg-black/[0.03] px-5 py-4 focus:outline-none focus:border-black dark:border-white/10 dark:bg-white/[0.04] dark:focus:border-white"
          ></textarea>

          <button
            type="submit"
            :disabled="submitState === 'loading'"
            class="w-full rounded-full border border-black py-4 text-xs uppercase tracking-[0.25em] transition hover:bg-black hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-black"
          >
            {{ submitState === "loading" ? "Sending..." : "Send Message" }}
          </button>

          <p
            v-if="formMessage"
            class="text-center"
            :class="
              submitState === 'error'
                ? 'text-amber-500 dark:text-amber-300'
                : 'text-green-500 dark:text-green-400'
            "
          >
            {{ formMessage }}
          </p>
        </form>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: "MobileLandingPage",
  props: {
    projects: {
      type: Array,
      required: true,
    },
    experience: {
      type: Array,
      required: true,
    },
    skills: {
      type: Array,
      required: true,
    },
    socialLinks: {
      type: Array,
      required: true,
    },
    contactCards: {
      type: Array,
      required: true,
    },
    cvUrl: {
      type: String,
      required: true,
    },
    totalSkillItems: {
      type: Number,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    formMessage: {
      type: String,
      required: true,
    },
    submitState: {
      type: String,
      required: true,
    },
    submitForm: {
      type: Function,
      required: true,
    },
  },
};
</script>
